import {
  type AdminOrderDestination,
  type MarkOrderDeliveredInput,
  markOrderDeliveredInputSchema,
} from '@repo/api-contracts';
import { useId, useState } from 'react';
import { formStyles, Input, Label, OrderActionDialog } from '@/shared/ui';

interface OrderDeliveredDialogProps {
  readonly orderId: string;
  readonly shippedOn: string;
  readonly today: string;
  /** 주문 배송지(정렬 순서) — 송장이 없는 배송지를 경고한다. 번호는 index + 1(배송지 블록과 같다). */
  readonly destinations: readonly AdminOrderDestination[];
  readonly onSubmit: (input: MarkOrderDeliveredInput) => Promise<unknown>;
  readonly onReload: () => void;
  readonly onClose: () => void;
  readonly pending?: boolean;
}

/**
 * 수동 배송 완료 — 배송 완료일(기본값: 한국 오늘)은 출고일부터 오늘까지다.
 * 배송 완료는 주문 단위라(spec D13) 송장 없는 배송지가 남아 있어도 서버는 받는다. 그러면 그 배송지에 송장을 더 넣을 수 없으므로
 * 그 배송지 목록을 경고하고, 확인 체크를 해야 처리 버튼이 열린다.
 */
export function OrderDeliveredDialog({
  orderId,
  shippedOn,
  today,
  destinations,
  onSubmit,
  onReload,
  onClose,
  pending,
}: OrderDeliveredDialogProps) {
  const id = useId();
  const [date, setDate] = useState(today);
  const [confirmed, setConfirmed] = useState(false);
  const unshipped = destinations.flatMap((destination, index) =>
    destination.shipments.length === 0 ? [`배송지 ${index + 1}`] : [],
  );
  const needsConfirm = unshipped.length > 0;
  return (
    <OrderActionDialog
      title="배송 완료"
      submitLabel="배송 완료 처리"
      onReload={onReload}
      onClose={onClose}
      pending={pending}
      submitDisabled={needsConfirm && !confirmed}
      validate={() =>
        !markOrderDeliveredInputSchema.safeParse({ orderId, deliveredOn: date }).success ||
        date < shippedOn ||
        date > today
          ? '배송 완료일은 출고일 이상, 오늘 이하여야 합니다.'
          : null
      }
      onSubmit={async () => {
        await onSubmit({ orderId, deliveredOn: date });
      }}
    >
      <div className={formStyles.field}>
        <Label htmlFor={`${id}-date`}>배송 완료일</Label>
        <Input
          id={`${id}-date`}
          type="date"
          min={shippedOn}
          max={today}
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
      </div>
      {needsConfirm ? (
        <div className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <p>
            {`송장이 없는 배송지 ${unshipped.length}곳이 있습니다: ${unshipped.join(', ')}. 배송 완료로 바꾸면 이 배송지에는 송장을 더 넣을 수 없습니다.`}
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={pending}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            송장 없는 배송지가 있음을 확인했습니다
          </label>
        </div>
      ) : null}
    </OrderActionDialog>
  );
}
